import {
  typeConverter,
  useFirestore,
  useFirestoreResults,
  useFirestoreSnapshot,
} from '@goschool/react-firebase'
import type {User} from 'firebase/auth'
import type {DocumentSnapshot, QueryDocumentSnapshot} from 'firebase/firestore'
import {
  addDoc,
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import {useCallback, useMemo} from 'react'
import type {Course} from '../Course'
import type {GoSchoolInvitation, GoSchoolInvitationAcceptance} from '../GoSchoolInvitation'
import type {GoSchoolUser} from '../GoSchoolUser'
import type {Organization} from '../Organization'

export function useInvitations(
  organization: QueryDocumentSnapshot<Organization> | null | undefined
) {
  const firestore = useFirestore()

  const q = useMemo(() => {
    if (organization == null) {
      return undefined
    }
    return query(
      collection(firestore, 'invitations').withConverter(
        typeConverter<GoSchoolInvitation>()
      ),
      where('organization', '==', organization.ref),
      orderBy('created_at', 'desc')
    )
  }, [firestore, organization])

  return useFirestoreResults(q)
}

export function useIssuedBy(
  invitationSnapshot: QueryDocumentSnapshot<GoSchoolInvitation> | null | undefined
): DocumentSnapshot<GoSchoolUser> | null | undefined {
  const firestore = useFirestore()

  const createdBy = invitationSnapshot?.data()?.created_by ?? null
  const docRef = useMemo(() => {
    if (invitationSnapshot == null || createdBy == null) {
      return undefined
    }
    return doc(firestore, 'users', createdBy).withConverter(
      typeConverter<GoSchoolUser>()
    )
  }, [invitationSnapshot, createdBy, firestore])

  const snapshot = useFirestoreSnapshot(docRef)
  return createdBy == null ? null : snapshot
}

export function useAccepted(
  invitationSnapshot: QueryDocumentSnapshot<GoSchoolInvitation> | null | undefined
) {
  const q = useMemo(() => {
    if (invitationSnapshot == null) {
      return undefined
    }
    return query(
      collection(invitationSnapshot.ref, 'acceptance').withConverter(
        typeConverter<GoSchoolInvitationAcceptance>()
      ),
      orderBy('accepted_at', 'desc')
    )
  }, [invitationSnapshot])

  return useFirestoreResults(q)
}

export function useCourseInstructorInvitations(
  courseSnapshot: QueryDocumentSnapshot<Course> | null | undefined,
  user: User | null | undefined
) {
  const firestore = useFirestore()

  const q = useMemo(() => {
    if (courseSnapshot == null || user == null) {
      return undefined
    }
    return query(
      collection(firestore, 'invitations').withConverter(
        typeConverter<GoSchoolInvitation>()
      ),
      where('course', '==', courseSnapshot.ref),
      where('created_by', '==', user.uid),
      // Keep this aligned with existing Firestore indexes: roles is stored as a small fixed array.
      where('roles', '==', ['instructor'])
    )
  }, [firestore, courseSnapshot, user])

  const invitations = useFirestoreResults(q)
  return {invitations}
}

export function useCourseStudentInvitations(
  courseSnapshot: QueryDocumentSnapshot<Course> | null | undefined,
  user: User | null | undefined
) {
  const firestore = useFirestore()

  const q = useMemo(() => {
    if (courseSnapshot == null || user == null) {
      return undefined
    }
    return query(
      collection(firestore, 'invitations').withConverter(
        typeConverter<GoSchoolInvitation>()
      ),
      where('course', '==', courseSnapshot.ref),
      where('created_by', '==', user.uid),
      where('email', '==', null),
      // Avoid requiring a new composite index (array-contains + orderBy).
      // Historically these invitations used fixed roles arrays.
      where('roles', '==', ['student'])
    )
  }, [firestore, courseSnapshot, user])

  const docs = useFirestoreResults(q)
  const invitation = useMemo(() => {
    if (docs === undefined) {
      return undefined
    }

    const now = Date.now()
    const activeInvitations = (docs ?? []).filter((snapshot) => {
      const expiresAt = snapshot.data().expires_at
      return expiresAt == null || expiresAt.toMillis() > now
    })

    if (activeInvitations.length === 0) {
      return null
    }

    return activeInvitations.reduce((latest, current) => {
      const latestExpiresAt = latest.data().expires_at?.toMillis() ?? Number.POSITIVE_INFINITY
      const currentExpiresAt = current.data().expires_at?.toMillis() ?? Number.POSITIVE_INFINITY
      return currentExpiresAt > latestExpiresAt ? current : latest
    })
  }, [docs])

  const createInvitation = useCallback(
    async (expirationInHours: number) => {
      if (courseSnapshot == null) {
        throw new Error('Course not loaded')
      }
      if (user == null) {
        throw new Error('User not authenticated')
      }

      const orgRef = courseSnapshot.ref.parent.parent
      if (orgRef == null) {
        throw new Error('Course not in organization')
      }

      await addDoc(collection(firestore, 'invitations'), {
        created_at: serverTimestamp(),
        created_by: user.uid,
        email: null,
        name: null,
        message: null,
        roles: ['student'],
        organization: orgRef.withConverter(typeConverter<Organization>()),
        course: courseSnapshot.ref,
        expires_at: Timestamp.fromMillis(
          Date.now() + 1000 * 60 * 60 * expirationInHours
        ),
      })
    },
    [courseSnapshot, firestore, user]
  )

  const extendInvitation = useCallback(
    async (expirationInHours: number) => {
      if (invitation == null) {
        throw new Error('Invitation does not exist')
      }
      await updateDoc(invitation.ref, {
        expires_at: Timestamp.fromMillis(
          Date.now() + 1000 * 60 * 60 * expirationInHours
        ),
      })
    },
    [invitation]
  )

  const invalidateInvitation = useCallback(
    async () => {
      if (invitation == null) {
        throw new Error('Invitation does not exist')
      }
      await updateDoc(invitation.ref, {
        expires_at: Timestamp.fromMillis(Date.now() - 1000),
      })
    },
    [invitation]
  )

  return {
    invitation,
    createInvitation,
    extendInvitation,
    invalidateInvitation,
  }
}
